import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import { useAuth } from '../../auth/AuthContext';
import { mapApiSessionSummary, useKurocoAiData } from '../../adapters/kurocoAiData';
import { fetchChatSessions } from '../../api/dashboardApi';
import useOwnDashboardData from '../../hooks/useOwnDashboardData';
import {
  buildChatDossier,
  getChatDossierMeta,
} from '../../utils/kpiSnapshot';
import { useGoalsConfig } from '../../utils/goalsLocalStore';
import AiHome from './AiHome';
import AiReview from './AiReview';
import AiRecord from './AiRecord';
import AiImprove from './AiImprove';
import AiHistory from './AiHistory';
import AiSessionView from './AiSessionView';

/**
 * KUROCO AI相談 — internal routes wired to /api/v1/ai/chat/*.
 * Chat is about the signed-in person's own record; falls back to the Home selection when unbound.
 */
export default function AiStack({ displayName, settingsPath, onSubScreenChange }) {
  const [route, setRoute] = useState('home');
  const [viewSessionId, setViewSessionId] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [chatFailedText, setChatFailedText] = useState(null);
  const [aiSessions, setAiSessions] = useState([]);
  const [sessionsError, setSessionsError] = useState(null);
  const { user } = useAuth();
  const {
    selectedStore,
    selectedStylist,
    selectedMonth,
    myProfile,
    kpis: viewKpis,
    kpiHistory: viewKpiHistory,
    mlForecasts: viewMlForecasts,
    storeAverageKpis: viewStoreAverageKpis,
  } = useDashboard();

  // The chat is the signed-in person's own: it is always about their own POS record
  // (home_store + stylist_name_pos), whatever Home is showing (owner decision 2026-10-04).
  // A user with no bound record (経営者, dev) keeps following the Home selection.
  const ownStore = myProfile?.home_store && myProfile?.stylist_name_pos ? myProfile.home_store : null;
  const ownStylist = ownStore ? myProfile.stylist_name_pos : null;
  const chatStore = ownStore || selectedStore;
  const chatStylist = ownStylist || selectedStylist;
  const viewIsOwn =
    (chatStore || null) === (selectedStore || null) && (chatStylist || null) === (selectedStylist || null);
  const own = useOwnDashboardData({
    store: chatStore,
    stylist: chatStylist,
    month: selectedMonth,
    enabled: Boolean(ownStore) && !viewIsOwn,
  });
  const kpis = viewIsOwn ? viewKpis : own.kpis;
  const kpiHistory = viewIsOwn ? viewKpiHistory : own.kpiHistory;
  const mlForecasts = viewIsOwn ? viewMlForecasts : own.mlForecasts;
  const storeAverageKpis = viewIsOwn ? viewStoreAverageKpis : own.storeAverageKpis;

  const [goals] = useGoalsConfig(user?.uid, chatStore, chatStylist);
  const chatName = chatStylist || displayName;
  const data = useKurocoAiData(chatName, aiSessions);

  // Dossier P0: 当月 + 店舗平均 + 目標・注力 + 履歴 + 予測 → existing kpi_summary (no extra BQ
  // when Home shows the user's own record; otherwise one fetch per month for their own data)
  const kpiSummary = useMemo(
    () =>
      buildChatDossier({
        kpis,
        selectedMonth,
        kpiHistory,
        mlForecasts,
        goals,
        storeAverageKpis,
      }),
    [kpis, selectedMonth, kpiHistory, mlForecasts, goals, storeAverageKpis]
  );
  const dossierMeta = useMemo(
    () =>
      getChatDossierMeta({
        selectedMonth,
        kpiHistory,
        mlForecasts,
        goals,
      }),
    [selectedMonth, kpiHistory, mlForecasts, goals]
  );

  // Chat history belongs to the signed-in user, not to the store/stylist being
  // viewed: a manager browsing the team must keep their own thread. Sessions are
  // already keyed by uid server-side, so listing unfiltered returns exactly the
  // caller's own sessions.
  const loadSessions = useCallback(async (force = false) => {
    try {
      setSessionsError(null);
      const res = await fetchChatSessions({ force });
      const todayStr = data.period.today;
      setAiSessions((res.sessions || []).map((s) => mapApiSessionSummary(s, todayStr)));
    } catch (err) {
      setSessionsError(err.message || '履歴の取得に失敗しました');
      setAiSessions([]);
    }
  }, [data.period.today]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const navigate = useCallback((next, id) => {
    setRoute(next);
    if (next === 'aisession' && id != null) {
      setViewSessionId(id);
    }
    if (next === 'home' && id != null) {
      // Continue from history: replace in-memory thread with that session (do not keep a "new" chat)
      setChatMessages([]);
      setChatError(null);
      setChatFailedText(null);
      setActiveSessionId(id);
    }
  }, []);

  const startNewChat = useCallback(() => {
    setActiveSessionId(null);
    setChatMessages([]);
    setChatError(null);
    setChatFailedText(null);
    setRoute('home');
  }, []);

  // The thread survives a store/stylist/month change on Home: the chat is the user's
  // own and is not wiped when a manager switches whom they are looking at. The session
  // is bound to the chat person (own record when bound, else the Home selection); the
  // dossier is only refreshed while it is still about that same person, so another
  // person's numbers never land in this thread.
  const activeSession = aiSessions.find((s) => s.id === activeSessionId) || null;
  const sessionMatchesSelection =
    !activeSessionId ||
    !activeSession ||
    ((activeSession.store || null) === (chatStore || null) &&
      (activeSession.stylist || null) === (chatStylist || null));

  useEffect(() => {
    const hideTab = !['home', 'aihistory'].includes(route);
    onSubScreenChange?.(hideTab);
    return () => onSubScreenChange?.(false);
  }, [route, onSubScreenChange]);

  const common = {
    data,
    onNavigate: navigate,
    settingsPath,
    store: chatStore,
    stylist: chatStylist,
    month: selectedMonth,
    kpiSummary,
    dossierMeta,
    onSessionsChanged: () => loadSessions(true),
    sessionsError,
    sessionMatchesSelection,
    activeSessionStore: activeSession?.store ?? null,
    activeSessionStylist: activeSession?.stylist ?? null,
    activeSessionId,
    setActiveSessionId,
    startNewChat,
    chatMessages,
    setChatMessages,
    chatSending,
    setChatSending,
    chatError,
    setChatError,
    chatFailedText,
    setChatFailedText,
  };

  switch (route) {
    case 'review':
      return <AiReview {...common} />;
    case 'record':
      return <AiRecord {...common} />;
    case 'improve':
      return (
        <AiImprove
          {...common}
          initialFlow="improve"
          onHistory={() => navigate('aihistory')}
        />
      );
    case 'aihistory':
      return <AiHistory {...common} />;
    case 'aisession':
      return <AiSessionView {...common} sessionId={viewSessionId} />;
    case 'home':
    default:
      return <AiHome {...common} onHistory={() => navigate('aihistory')} />;
  }
}
